import type { CallReason, CallResolution } from '@inbound/contract';
import { callReasonLabels, callResolutionLabels } from '@inbound/contract';

import styles from './OutcomeChips.module.css';

/**
 * Why the caller rang, as a chip (S08).
 *
 * Reasons are categories rather than verdicts, so they share one neutral tint;
 * only a complaint stands out. `UNKNOWN` gets its own muted treatment: it means
 * the analyzer could not decide, and dressing that as a finding would mislead.
 */
export function ReasonChip({ reason }: { reason: CallReason }): JSX.Element {
  const tone =
    reason === 'UNKNOWN' ? styles.muted : reason === 'COMPLAINT' ? styles.bad : styles.info;
  return <span className={`${styles.chip} ${tone}`}>{callReasonLabels[reason]}</span>;
}

/**
 * How the call ended for the caller, as a chip.
 *
 * Coloured by what it asks of an operator: an escalation is the loudest, a
 * callback next, an unresolved call a warning, a resolved one calm. A call
 * forwarded to a colleague has its own tint: a person already has it.
 */
export function ResolutionChip({ resolution }: { resolution: CallResolution }): JSX.Element {
  const tone = {
    RESOLVED: styles.good,
    UNRESOLVED: styles.warn,
    ESCALATION_REQUESTED: styles.bad,
    CALLBACK_REQUESTED: styles.info,
    TRANSFERRED: styles.handed,
    UNKNOWN: styles.muted,
  }[resolution];
  return <span className={`${styles.chip} ${tone}`}>{callResolutionLabels[resolution]}</span>;
}
